/**
 * Typed report lines beyond food-status — the keypoint (🥖) and queue (⚪) rows.
 *
 * Each definition mirrors its backend generator so the form regenerates the exact
 * wording from semantic variables, applying the same conditions/calculations:
 *   keypoints → services/.../daily_report/sections/empty_shelf.py (performance block)
 *   queue     → services/.../daily_report/sections/checkout_control.py
 *
 * A def declares the editable fields, a `match` that parses one row into
 * {fields, meta} (meta preserves non-editable bits: emoji, genitive, href, derived
 * open time), and a `serialize` that rebuilds the row. Grammar (genitive, category
 * name) is preserved from meta — validators never decline Russian by hand.
 */

// Queue (⚪) single-line templates. Keypoints (🥖) are a multi-row block — see
// keypointBlock.ts — because their variants add/remove whole rows.
export type TypedKind = 'q_longest' | 'q_novisit' | 'q_longqueue';

export interface FieldSpec {
  key: string;
  labelKey: string;
  kind: 'percent' | 'number' | 'time' | 'toggle';
  showIf?: (f: TypedFields) => boolean;
}

export type TypedFields = Record<string, number | string | boolean>;
export type TypedMeta = Record<string, string>;

export interface TypedDef {
  kind: TypedKind;
  titleKey: string;
  hasLinkPeriod?: boolean;
  fields: FieldSpec[];
  match: (row: string) => { fields: TypedFields; meta: TypedMeta } | null;
  serialize: (fields: TypedFields, meta: TypedMeta) => string;
}

// ── time helpers (МСК, whole minutes as the generator uses) ──────────────────
export function timeToMin(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
export function afterOpenTxt(totalMins: number): string {
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h && m !== 0) return `${h}ч ${m} мин. после открытия`;
  if (h && m === 0) return `${h}ч после открытия`;
  return `${m} мин. после открытия`;
}
export function afterTxtToMins(s: string): number | null {
  const h = /(\d+)\s*ч/.exec(s);
  const m = /(\d+)\s*мин/.exec(s);
  if (!h && !m) return null;
  return (h ? Number(h[1]) : 0) * 60 + (m ? Number(m[1]) : 0);
}
const num = (v: TypedFields[string]) => Math.max(0, Math.round(Number(v) || 0));

export const TYPED_DEFS: TypedDef[] = [
  {
    kind: 'q_longest',
    titleKey: 'reportValidation.tf.q_longest',
    hasLinkPeriod: true,
    fields: [
      { key: 'people', labelKey: 'reportValidation.tf.peak', kind: 'number' },
      { key: 'minutes', labelKey: 'reportValidation.tf.drainMin', kind: 'number' },
    ],
    match: (row) => {
      const m =
        /^⚪\s+(.+?)\s+очередь\s+<b>\s*(\d+)\s+человек\s*<\/b>,\s+спала за\s+<b>\s*(\d+)\s+мин\.\s*<\/b>\s*$/u.exec(
          row,
        );
      if (!m) return null;
      const prefix = m[1];
      const a = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/iu.exec(prefix);
      return {
        fields: { people: Number(m[2]), minutes: Number(m[3]) },
        meta: { href: a?.[1] ?? '', label: a ? a[2] : prefix.trim() },
      };
    },
    serialize: (f, meta) => {
      const prefix = meta.href ? `<a href='${meta.href}'>${meta.label}</a>` : meta.label;
      return `⚪ ${prefix} очередь <b>${num(f.people)} человек</b>, спала за <b>${num(f.minutes)} мин.</b>`;
    },
  },
  {
    kind: 'q_novisit',
    titleKey: 'reportValidation.tf.q_novisit',
    fields: [
      { key: 'hours', labelKey: 'reportValidation.tf.hoursNoVisit', kind: 'number' },
      { key: 'total', labelKey: 'reportValidation.tf.totalHours', kind: 'number' },
    ],
    match: (row) => {
      const m =
        /^⚪\s+Посетителей не было у кассы\s+<b>\s*(\d+)\s+часов\s*<\/b>\s+из\s+<b>\s*(\d+)\s*<\/b>\s*$/u.exec(
          row,
        );
      return m ? { fields: { hours: Number(m[1]), total: Number(m[2]) }, meta: {} } : null;
    },
    serialize: (f) =>
      `⚪ Посетителей не было у кассы <b>${num(f.hours)} часов</b> из <b>${num(f.total)}</b>`,
  },
  {
    kind: 'q_longqueue',
    titleKey: 'reportValidation.tf.q_longqueue',
    fields: [
      { key: 'threshold', labelKey: 'reportValidation.tf.threshold', kind: 'number' },
      { key: 'events', labelKey: 'reportValidation.tf.events', kind: 'number' },
      { key: 'secondOn', labelKey: 'reportValidation.tf.secondCashierOn', kind: 'toggle' },
      {
        key: 'second',
        labelKey: 'reportValidation.tf.secondCashier',
        kind: 'number',
        showIf: (f) => Boolean(f.secondOn) && num(f.events) > 0,
      },
    ],
    match: (row) => {
      const none = /^⚪\s+Очередь длиннее\s+<b>\s*(\d+)\s*<\/b>\s+человек не наблюдалась\s*$/u.exec(row);
      if (none) return { fields: { threshold: Number(none[1]), events: 0, secondOn: false, second: 0 }, meta: {} };
      const m =
        /^⚪\s+Очередь длиннее\s+<b>\s*(\d+)\s*<\/b>\s+человек дольше минуты:\s+<b>\s*(\d+)\s+раз\s*<\/b>(?:,\s+открыли вторую кассу:\s+<b>\s*(\d+)\s+раз\s*<\/b>)?\s*$/u.exec(
          row,
        );
      if (!m) return null;
      return {
        fields: {
          threshold: Number(m[1]),
          events: Number(m[2]),
          secondOn: m[3] != null,
          second: m[3] != null ? Number(m[3]) : 0,
        },
        meta: {},
      };
    },
    serialize: (f) => {
      const threshold = num(f.threshold);
      const events = num(f.events);
      if (events === 0) return `⚪ Очередь длиннее <b>${threshold}</b> человек не наблюдалась`;
      let msg = `⚪ Очередь длиннее <b>${threshold}</b> человек дольше минуты: <b>${events} раз</b>`;
      if (f.secondOn) msg += `, открыли вторую кассу: <b>${num(f.second)} раз</b>`;
      return msg;
    },
  },
];

export function matchTypedLine(
  row: string,
): { typedKind: TypedKind; fields: TypedFields; meta: TypedMeta } | null {
  for (const def of TYPED_DEFS) {
    const r = def.match(row);
    if (r) return { typedKind: def.kind, fields: r.fields, meta: r.meta };
  }
  return null;
}

export function defFor(kind: TypedKind): TypedDef {
  return TYPED_DEFS.find((d) => d.kind === kind)!;
}
